import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { getFilteredTableData } from "../../../../../shared/utils/tableUtils"
import { makeStyles } from "tss-react/mui"
import { Grid, Tooltip } from "@mui/material"
import { faIdCard } from "@fortawesome/pro-light-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { MRT_ColumnDef, MRT_ColumnFiltersState, MRT_RowSelectionState, MRT_TableInstance } from "material-react-table"
import useActiveFiltersCountAccounting from "../../../../../shared/components/filters/accountingFiltersDrawer/useActiveFiltersCountAccounting"
import { useInitialDateFilters } from "../../../../../shared/components/table/useInitialDateFilters"
import { AccountingQuery, fetchAccountingOrdersByPeriod, FetchAccountingOrdersResponse } from "../accountingApi"
import { StandardExportButton } from "../../../../../shared/components/table/StandardExportButton"
import { StandardTwoLinesCell } from "../../../../../shared/components/table/StandardTwoLinesCell"
import { StandardFilterButton } from "../../../../../shared/components/table/StandardFilterButton"
import { StandardTableFilter } from "../../../../../shared/components/table/StandardTableFilters"
import { StandardTabsRowActions } from "../../../../../shared/components/tabs/StandardTabsRowActions"
import { ExpandableSearch } from "../../../../../shared/components/filters/ExpandableSearch"
import { ORDERS_SEARCH_MIN_LENGTH } from "../../../../../shared/components/filters/constants/searchConst"
import {
    AccountingFilterProps,
    AccountingFiltersDrawer,
    defaultAccountingFilterOptionsList,
} from "../../../../../shared/components/filters/accountingFiltersDrawer/AccountingFiltersDrawer"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import {
    DateType,
    SelectDateTypeFilter,
} from "../../../../../shared/components/filters/selectDateTypeFilter/SelectDateTypeFilter"
import { StandardOrderTypeFilter } from "../../../../../shared/components/filters/orderTypeFilter/StandardOrderTypeFilter"
import {
    canteenTakeawayFilterButtonConfig,
    CanteenTakeawayFilterType,
} from "../../../../../shared/components/filters/orderTypeFilter/helpers"
import { MatchEnvironment } from "../../../../environment/components/MatchEnvironment"
import { EnvironmentType } from "../../../../environment/envTypes"
import FloatingOrderActionBar from "../../../merchant/production/components/FloatingOrderActionBar"
import { useExportOrdersToCsv } from "../../../merchant/production/components/ExportFunctionOrder"
import { useRefreshBeforeExport } from "../../../merchant/production/components/useRefreshBeforeExport"
import { ApiQueryTable } from "../../../../../core/api/components/ApiQueryTable"
import { useColumnFactory } from "../../../../../core/api/useTableColumns"
import { TableToolbarBanner } from "../../../merchant/production/components/TableToolbarBanner"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { PaymentMethodCell } from "../../../merchant/production/components/PaymentMethodCell"
import { Receipt, ReceiptDialog } from "../../../../receipt/pages/ReceiptDialog"
import { ChannelTypeChip } from "../../../merchant/production/components/ChannelTypeChip"
import { tailwindPalette } from "../../../../theme/tailwindPalette"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { useMerchant } from "../../../../merchants/merchantHooks"
import { Order } from "../../../controlKitchenTypes"
import {
    TableNumberOfItemsBox,
    TableSummaryWrap,
    TableTotalAmountBox,
} from "../../../../../shared/components/table/TableSummaryBox"
import { useDateTime } from "../../../../localization/useDateTime"
import { COLUMN_SIZE } from "../../../../../shared/constants/table/config"
import {
    type DtDateRange,
    StandardDateRangeFilter,
} from "../../../../../shared/components/table/StandardDateRangeFilter"
import { OrderAccountingMenu } from "./OrderAccountingMenu"
import { useOrderRefundFlow } from "./useOrderRefundFlow"

const useStyles = makeStyles()(() => ({
    idCardIcon: {
        marginLeft: ".5rem",
        cursor: "pointer",
        color: tailwindPalette.sky[500],
    },
    table: {
        ".Mui-TableHeadCell-Content-Labels": {
            paddingLeft: 0,
        },
    },
}))

export enum AccountingOrdersTab {
    TRANSACTIONS = "TRANSACTIONS",
    REFILL = "REFILL",
}

type OrderAccountingTabProps = Readonly<{
    tabQueryParam?: AccountingOrdersTab
}>
export type FilterOrdersProps = Readonly<{
    shopsIds: string[]
    customersId?: { customerId: string | undefined; groupId: string | undefined }
    orderDetails?: {
        orderNumber: null | number
        user: string
    }
}>
const selectOrders = (data: FetchAccountingOrdersResponse) => data?.orders || []

export function OrderAccountingTab({ tabQueryParam }: OrderAccountingTabProps) {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()
    const { merchantId } = useMerchant()

    const showOrderTypeFilter = tabQueryParam === AccountingOrdersTab.TRANSACTIONS

    const [receipt, setReceipt] = useState<Receipt>()
    const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([])
    const [search, setSearch] = useState("")
    const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({})
    const [showNotPrintedOrders, setShowNotPrintedOrders] = useState(false)
    const [drawerOpen, setDrawerOpen] = useState(false)
    const [selectedRows, setSelectedRows] = useState<Order[]>()
    const tableInstanceRef = useRef<MRT_TableInstance<Order>>(null)
    const [dateType, setDateType] = useState<DateType>(DateType.ORDER_DATE)
    const [orderType, setOrderType] = useState<CanteenTakeawayFilterType>(CanteenTakeawayFilterType.ALL)
    const dateTimeFactory = useDateTime()
    const today = dateTimeFactory.now()
    const { startDate, endDate } = useInitialDateFilters()
    const [filtersList, setFiltersList] = useState<AccountingFilterProps>({
        ...defaultAccountingFilterOptionsList,
        merchantId: merchantId.toString(),
    })
    const [dateState, setDateState] = useState<DtDateRange>({
        startDate,
        endDate,
    })

    const { activeCount } = useActiveFiltersCountAccounting(filtersList)

    // a search or an order id is for an order whose date is unknown, so the period must not narrow it
    const searchQuery = search.length >= ORDERS_SEARCH_MIN_LENGTH ? search : undefined
    const isSearchingAllDates = Boolean(filtersList.orderId) || Boolean(searchQuery)

    const orderList = useApiQuery(fetchAccountingOrdersByPeriod, {
        queryName: AccountingQuery.ORDERS_TRANSACTIONS_LIST,
        dependencies: {
            ...filtersList,
            merchantId,
            tab: tabQueryParam,
            search: searchQuery,
            merchantIds: filtersList.merchantIds?.join(","),
            shopIds: filtersList.shopIds?.join(","),
            paymentMethods: filtersList.paymentMethods?.join(","),
            startDate: isSearchingAllDates ? undefined : dateState.startDate?.toISODate(),
            endDate: isSearchingAllDates ? undefined : dateState.endDate?.toISODate(),
            dateType,
            orderType: orderType === CanteenTakeawayFilterType.ALL ? undefined : orderType,
        },
        select: selectOrders,
    })

    const { requestRefund, dialogs: refundDialogs } = useOrderRefundFlow()

    const columnFactory = useColumnFactory<Order>()

    const columns = useMemo<MRT_ColumnDef<Order>[]>(
        () => [
            columnFactory.idColumn({
                header: l10n.getString("production-order-number"),
                accessorKey: "id",
            }),
            columnFactory.datetimeColumn(
                {
                    header: l10n.getString("production-order-time"),
                    accessorKey: "created",
                },
                (row) => row.created,
            ),
            columnFactory.nameColumn({
                header: l10n.getString("office-purchase-name"),
                accessorKey: "customer.displayName",
                Cell: ({ row }) => (
                    <StandardTwoLinesCell
                        title={
                            <>
                                {row.original?.customer?.displayName ?? ""}
                                <Tooltip arrow title={row.original.paymentDetails.idCard?.cardNumber}>
                                    {row.original.paymentDetails.idCard?.cardNumber ? (
                                        <FontAwesomeIcon className={classes.idCardIcon} icon={faIdCard} size="lg" />
                                    ) : (
                                        <></>
                                    )}
                                </Tooltip>
                            </>
                        }
                        subtitle1={row.original.customer?.employeeId ?? ""}
                        subtitle2={row.original.customer?.email}
                    />
                ),
            }),
            columnFactory.typeColumn({
                header: l10n.getString("office-purchase-payment-channel"),
                accessorKey: "paymentDetails.channel.displayText",
                Cell: ({ row }) => <ChannelTypeChip channel={row.original.paymentDetails.channel} />,
            }),
            columnFactory.typeColumn({
                size: COLUMN_SIZE.SMM,
                maxSize: COLUMN_SIZE.SMM,
                header: l10n.getString("production-order-payment-status"),
                accessorKey: "paymentDetails.method",
                Cell: ({ row }) => <PaymentMethodCell paymentDetails={row.original.paymentDetails} />,
            }),
            columnFactory.moneyColumn(
                {
                    header: l10n.getString("office-purchase-amount"),
                    id: "amount",
                    size: COLUMN_SIZE.S,
                    maxSize: COLUMN_SIZE.S,
                },
                (row) => row.amount,
                true,
            ),
        ],
        [],
    )

    function showReceipt(row: Order) {
        setReceipt({
            orderId: row.id,
            orderUid: row.uid,
            orderType: row.orderType,
        })
    }

    useEffect(() => {
        if (Object.keys(rowSelection).length > 0) {
            setSelectedRows(tableInstanceRef.current?.getSelectedRowModel().rows.map((row) => row.original))
        } else {
            setSelectedRows(undefined)
        }
    }, [rowSelection])

    const hasNonePrinted = () => {
        if (!orderList.data) {
            return
        }
        return (
            orderList.data.filter((order) => {
                return order.printDetails?.isPrinted === false
            }).length > 0
        )
    }

    const showAlertBanner = useMemo(() => hasNonePrinted(), [orderList.data])

    const exportOrder = useExportOrdersToCsv({
        data: orderList.data,
        startDate: dateState.startDate ?? today,
        endDate: dateState.endDate ?? today,
    })
    const { exportFresh, isExporting } = useRefreshBeforeExport(orderList, exportOrder)

    const handleClearSelected = useCallback(() => {
        setSelectedRows(undefined)
        setRowSelection({})
    }, [])

    // row selection is index-based, so it must not outlive the list it was made on
    useEffect(() => {
        handleClearSelected()
    }, [orderList.data, handleClearSelected])

    return (
        <>
            <StandardTabsRowActions>
                <LocalizedStrict id="accounting-orders-search-field" attrs={{ label: true, placeholder: true }}>
                    <ExpandableSearch
                        value={search}
                        onChange={setSearch}
                        label="Search order"
                        placeholder="Type order id, name or email."
                        bordered
                    />
                </LocalizedStrict>
                <StandardFilterButton onClick={() => setDrawerOpen(true)} activeCount={activeCount} bordered />
                <StandardExportButton onClick={exportFresh} loading={isExporting} bordered />
            </StandardTabsRowActions>
            <Grid container direction="column" wrap="nowrap" className={classes.table}>
                {showAlertBanner && (
                    <TableToolbarBanner
                        showNotPrintedOrders={showNotPrintedOrders}
                        setShowNotPrintedOrders={setShowNotPrintedOrders}
                    />
                )}

                <ApiQueryTable
                    isLoading={orderList.isInitialLoading}
                    isFetching={orderList.isFetching}
                    isError={orderList.isError}
                    data={orderList.data ?? []}
                    tableInstanceRef={tableInstanceRef}
                    columns={columns}
                    onColumnFiltersChange={setColumnFilters}
                    onRowSelectionChange={setRowSelection}
                    enableRowSelection
                    initialState={{ density: "spacious" }}
                    renderRowActionMenuItems={({ row, closeMenu }) => [
                        <OrderAccountingMenu
                            order={row.original}
                            onShowReceipt={showReceipt}
                            onRefund={requestRefund}
                            onClose={closeMenu}
                        />,
                    ]}
                    renderToolbarInternalActions={({ table }) => (
                        <StandardTableFilter variant="right" table={table} showGlobalFilterButton={false} />
                    )}
                    renderTopToolbarCustomActions={() => (
                        <StandardTableFilter variant="left">
                            {showOrderTypeFilter && (
                                <MatchEnvironment
                                    environments={[
                                        EnvironmentType.Local,
                                        EnvironmentType.Test,
                                        EnvironmentType.Demo,
                                        EnvironmentType.QA,
                                    ]}
                                >
                                    <StandardOrderTypeFilter
                                        isMarginBottom={false}
                                        orderType={orderType}
                                        setOrderType={setOrderType}
                                        config={canteenTakeawayFilterButtonConfig}
                                    />
                                </MatchEnvironment>
                            )}
                            <SelectDateTypeFilter dateType={dateType} setDateType={setDateType} />
                            <StandardDateRangeFilter value={dateState} onChange={setDateState} />
                        </StandardTableFilter>
                    )}
                    renderBottomToolbarCustomActions={({ table }) => (
                        <TableSummaryWrap padding="sm">
                            <LocalizedStrict id="table-summary-box-number-of-orders" attrs={{ title: true }}>
                                <TableNumberOfItemsBox
                                    data={table.getFilteredRowModel().rows}
                                    isLoading={orderList.isLoading}
                                    title="Number of orders"
                                />
                            </LocalizedStrict>

                            <TableTotalAmountBox
                                data={table.getFilteredRowModel().rows}
                                isLoading={orderList.isLoading}
                                accessor={(row) => row.original.amount}
                            />
                        </TableSummaryWrap>
                    )}
                    state={{
                        columnFilters,
                        rowSelection,
                    }}
                />
            </Grid>

            {selectedRows && (
                <FloatingOrderActionBar
                    selectedRows={selectedRows}
                    startDate={dateState.startDate ?? today}
                    endDate={dateState.endDate ?? today}
                    ordersQuery={orderList}
                    handleClearSelected={handleClearSelected}
                    hidePrinterQueueAction
                />
            )}

            {receipt && (
                <ReceiptDialog
                    receipt={receipt}
                    orders={getFilteredTableData(tableInstanceRef)}
                    open={receipt !== undefined}
                    onClose={() => setReceipt(undefined)}
                />
            )}

            <AccountingFiltersDrawer
                setFiltersList={setFiltersList}
                drawerOpen={drawerOpen}
                setDrawerOpen={setDrawerOpen}
                filtersList={filtersList}
            />

            {refundDialogs}
        </>
    )
}
